import React, { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Flex, Image, Text } from 'rebass';
import argGisNewItem from '../../assets/arcgis-new-item.png';
import argGisScreenshot from '../../assets/arcgis-url-option.png';
import Modal, { ModalBody } from '../../components/Modal';
import { Input } from '../../components/Form';
import { CopyButton } from '../../components/CopyButton/CopyButton';
import { gql } from '@/modes/waterSupplier/gql';

type OgcAssembliesUrlModalProps = {
  onClose: () => void;
};

const GENERATE_OGC_ASSEMBLIES_URLS = gql(`
  mutation GenerateOgcAssembliesUrls {
    generateOgcAssembliesUrls {
      ogcUrl
      geojsonUrl
    }
  }
`);

/**
 * Lets a water supplier generate a signed OGC API Features server URL
 * (the landing page of the discovery chain, not a single dataset), ready
 * to paste into ArcGIS as an "OGC feature layer" ("Add Layer from URL").
 * Currently exposes one collection (backflow assemblies), but the URL
 * itself is server-scoped so later collections show up automatically.
 */
const OgcAssembliesUrlModal: React.FC<OgcAssembliesUrlModalProps> = ({
  onClose,
}) => {
  const [generateUrls, { data, loading, error }] = useMutation(
    GENERATE_OGC_ASSEMBLIES_URLS
  );

  useEffect(() => {
    generateUrls();
  }, [generateUrls]);

  const ogcUrl = data?.generateOgcAssembliesUrls?.ogcUrl ?? '';
  const geojsonUrl = data?.generateOgcAssembliesUrls?.geojsonUrl ?? '';

  return (
    <Modal title="ArcGIS: OCG/GeoJSON URL" onClose={onClose}>
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Box as="ol" pl={4} sx={{ '& li': { pb: 2 } }}>
            <Text as="li" variant="body1">
              Go to the "Content" page and click <strong>[+] New Item</strong>
              <br />
              <Image
                src={argGisNewItem}
                alt="ArcGIS"
                width="400px"
                sx={{ alignSelf: 'center', border: '1px solid #ccc' }}
              />
            </Text>
            <Text as="li" variant="body1">
              Choose{' '}
              <strong>
                &ldquo;URL &rarr; Link to an ArcGIS Server web service...
              </strong>
              <br />
              <Image
                src={argGisScreenshot}
                alt="ArcGIS"
                width="400px"
                sx={{ alignSelf: 'center', border: '1px solid #ccc' }}
              />
            </Text>
            <Text as="li" variant="body1">
              Paste this URL
              <br />
              {loading && <Text variant="body1">Generating URL...</Text>}
              {error && (
                <Text variant="body1" color="danger">
                  Failed to generate URL: {error.message}
                </Text>
              )}
              {ogcUrl && (
                <Flex alignItems="center" sx={{ gap: 1 }}>
                  <Input
                    readOnly
                    value={ogcUrl}
                    onFocus={(e) => e.target.select()}
                    sx={{ flexGrow: 1 }}
                  />
                  <CopyButton text={ogcUrl} label="ArcGIS URL" />
                </Flex>
              )}
            </Text>
            <Text as="li" variant="body1">
              Set the type to <strong>&ldquo;OGC feature layer&rdquo;</strong>
            </Text>
          </Box>
          <Text pt={5} variant="body1">
            Or, for a one-time import of your active assemblies, use this URL
            and select GeoJSON:
          </Text>
          {geojsonUrl && (
            <Flex alignItems="center" sx={{ gap: 1 }}>
              <Input
                readOnly
                value={geojsonUrl}
                onFocus={(e) => e.target.select()}
                sx={{ flexGrow: 1 }}
              />
              <CopyButton text={geojsonUrl} label="GeoJSON URL" />
            </Flex>
          )}
        </Flex>
      </ModalBody>
    </Modal>
  );
};

export default OgcAssembliesUrlModal;
